import type { FC, ReactNode } from 'react'
import type { Estimate } from '@/lib/pricing'

import {
    Clock,
    Phone,
    PhoneCall,
    ShieldCheck,
    Wrench
} from 'lucide-react'

import { BrandMark } from '@/components/generals/brand-mark'
import { Button, ButtonLink } from '@/components/generals/button'
import { formatEuro, formatHours } from '@/lib/format'

import { garage } from '@/constants/garage'

interface QuoteTicketProps {
    estimate: Estimate
    vehicleLabel: string
    onCallback: () => void
}

// Le ticket reprend la présentation du devis papier : lignes, pièces, main-d'œuvre, TVA.
export const QuoteTicket: FC<QuoteTicketProps> = ({ estimate, vehicleLabel, onCallback }): ReactNode => {
    const isEmpty = estimate.lines.length === 0

    return (
        <article aria-labelledby='ticket-title' className='rounded-xl border border-border bg-card shadow-float'>
            <header className='flex items-start justify-between gap-4 border-b border-dashed border-border p-5 sm:p-6'>
                <div className='flex items-center gap-3'>
                    <BrandMark className='size-9' />

                    <div>
                        <h3 id='ticket-title' className='font-semibold leading-tight'>
                            Estimation détaillée
                        </h3>

                        <p className='label-mono mt-1 text-muted-foreground'>
                            Réf. {estimate.reference} · {vehicleLabel}
                        </p>
                    </div>
                </div>

                <p className='label-mono rounded-sm border border-foreground/20 px-2 py-1 text-[0.625rem]'>
                    Prix TTC
                </p>
            </header>

            <div className='p-5 sm:p-6'>
                {isEmpty ? (
                    <div className='py-6 text-center'>
                        <Wrench aria-hidden='true' className='mx-auto size-6 text-muted-foreground' />

                        <p className='mt-3 font-semibold'>
                            Aucune intervention cochée
                        </p>

                        <p className='mx-auto mt-1 max-w-[18rem] text-sm text-muted-foreground'>
                            Choisissez au moins une intervention pour voir le détail du devis.
                        </p>
                    </div>
                ) : (
                    <ul className='space-y-4'>
                        {estimate.lines.map(line => (
                            <li key={line.id}>
                                <div className='flex items-baseline gap-2'>
                                    <span className='font-semibold leading-snug'>
                                        {line.name}
                                    </span>

                                    <span aria-hidden='true' className='min-w-4 flex-1 border-b border-dotted border-foreground/25' />

                                    <span className='font-mono text-sm font-semibold whitespace-nowrap tabular-nums'>
                                        {line.free ? 'Offert' : formatEuro(line.ttc, true)}
                                    </span>
                                </div>

                                <p className='mt-1 font-mono text-xs leading-relaxed text-muted-foreground tabular-nums'>
                                    {line.free
                                        ? `Temps atelier ${formatHours(line.hours)}, offert`
                                        : `Pièces ${formatEuro(line.partsTtc, true)} · main-d’œuvre ${formatHours(line.hours)}, ${formatEuro(line.laborTtc, true)}`}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className='border-t border-dashed border-border p-5 sm:p-6'>
                <dl className='space-y-1.5 font-mono text-sm tabular-nums'>
                    <div className='flex justify-between gap-4'>
                        <dt className='text-muted-foreground'>Total HT</dt>
                        <dd>{formatEuro(estimate.totalHt, true)}</dd>
                    </div>

                    <div className='flex justify-between gap-4'>
                        <dt className='text-muted-foreground'>Dont TVA 20&nbsp;%</dt>
                        <dd>{formatEuro(estimate.vat, true)}</dd>
                    </div>
                </dl>

                <div className='mt-5 flex items-end justify-between gap-4' aria-live='polite' aria-atomic='true'>
                    <p className='label-mono pb-1.5 text-muted-foreground'>
                        Total TTC estimé
                    </p>

                    <p
                        key={estimate.totalTtc}
                        className='heading text-[2.5rem] leading-none tabular-nums tracking-[-0.03em] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300'
                    >
                        {formatEuro(estimate.totalTtc)}
                    </p>
                </div>
            </div>

            <div className='space-y-3 rounded-b-xl border-t border-border bg-surface/60 p-5 sm:p-6'>
                <p className='flex items-start gap-2.5 text-sm'>
                    <Clock aria-hidden='true' className='mt-0.5 size-4 shrink-0' />

                    <span>
                        <span className='font-semibold'>{estimate.turnaround}</span>

                        {!isEmpty && (
                            <span className='text-muted-foreground'> · temps atelier {formatHours(estimate.hours)}</span>
                        )}
                    </span>
                </p>

                <p className='flex items-start gap-2.5 text-sm text-muted-foreground'>
                    <ShieldCheck aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-foreground' />
                    Prix ferme confirmé par écrit après lecture de votre carte grise. Estimation valable 30 jours.
                </p>

                <div className='grid gap-3 pt-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2'>
                    <ButtonLink href={garage.phone.href} className='w-full'>
                        <Phone aria-hidden='true' className='size-4' />
                        Réserver par téléphone
                    </ButtonLink>

                    <Button variant='secondary' onClick={onCallback} className='w-full' aria-haspopup='dialog'>
                        <PhoneCall aria-hidden='true' className='size-4' />
                        Être rappelé
                    </Button>
                </div>
            </div>
        </article>
    )
}
